// What a Connect click — or a connection end dragged — attaches to
// (docs/claude/connections.md *What a pick attaches to*), in ONE order: a part's
// declared connection point within reach; else a snapped point that carries an
// anchor GLUES there (a part's corner or side, a room corner, a polygon / wall
// vertex); else a snapped point on an object's outline glues along it; else the
// object under the cursor (in Select's pick order, its centre when within reach);
// anywhere else is a free point. Alt suspends every snap. Pure (no DOM): the app
// gives the object snap, its hit tests and what is hidden (`ConnPickEnv`).

import type {
  ConnectionType,
  ConnEnd,
  ConnOn,
  ConnPort,
  DimAnchor,
  Drawing,
  Flooring,
  Polygon,
  ProductInstance,
  TextComment,
  Vec2,
  Wall,
} from '../shared/types';
import {
  CONN_HOST_KINDS,
  type ConnHostKind,
  connOutline,
  objectCentre,
  onPoint,
} from './connections';
import {
  instanceToWorld,
  pointInPolygon,
  toInstanceLocal,
} from './geometry';
import {
  heaterWallBands,
  heaterWallsOf,
  isPolyRoom,
} from './polyRoom';
import { roomDoors } from './door';
import { textBoxCorners } from './textBox';
import { wallThicknessOf } from './dent';
import {
  alongAt,
  anchorObjectId,
} from './anchors';
import {
  doorHit,
  roomDoor,
} from './roomDoor';

/** Where a pick attaches a connection end: the object (`''` none: a free
 *  point), the end to store, where it stands, and the discipline of a port. */
export interface ConnPick { id: string; end?: ConnEnd; p: Vec2; type?: ConnectionType }
/** The object under the cursor as an end, and the dots it offers. */
export type ConnBody = { pick: ConnPick; dots: Vec2[] };

/** What a pick reads beyond the drawing: the app's object snap, its hit tests
 *  (the topmost object of a kind under a point, as Select finds it) and what it
 *  leaves out. */
export interface ConnPickEnv {
  drawing: Drawing;
  /** The snap aperture (mm): a port or an object's centre within it is caught. */
  aperture: number;
  /** How far round a door a press still takes it (mm). */
  featureTol: number;
  /** How far off an outline a snapped point may lie and still be on it (mm). */
  outlineTol: number;
  /** Whether a part takes an end: not frozen, its discipline shown, not hidden. */
  partShown(inst: ProductInstance): boolean;
  /** The plan's object snap at a point, and the anchor it carries. */
  snap(world: Vec2): { point: Vec2; snapped?: boolean; anchor?: DimAnchor };
  textAt(world: Vec2): TextComment | undefined;
  partAt(world: Vec2): ProductInstance | undefined;
  roomAt(world: Vec2): ProductInstance | undefined;
  polygonAt(world: Vec2): Polygon | undefined;
  wallAt(world: Vec2): Wall | undefined;
  floorAt(world: Vec2): Flooring | undefined;
  /** A part's anchored snap points (its corners, its sides' middles). */
  anchors(part: ProductInstance): Vec2[];
  /** An object's centre caught: the app shows its marker. */
  markCentre?(p: Vec2): void;
}

const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** A part's declared connection points in world space. */
export function portsWorld(inst: ProductInstance): Array<{ port: ConnPort; p: Vec2 }> {
  return (inst.ports ?? []).map((port) => ({ port, p: instanceToWorld(inst, { x: port.nx * inst.width / 2, y: port.ny * inst.depth / 2 }) }));
}

/** The declared connection point within the aperture of `world`, nearest first —
 *  a port wins over every other snap: it is what the product offers. */
export function portAt(env: ConnPickEnv, world: Vec2): { inst: ProductInstance; port: ConnPort; p: Vec2 } | null {
  let best: { inst: ProductInstance; port: ConnPort; p: Vec2; d: number } | null = null;
  for (const inst of env.drawing.instances) {
    if (!env.partShown(inst)) continue;
    for (const { port, p } of portsWorld(inst)) {
      const d = dist(world, p);
      if (d <= env.aperture && (!best || d < best.d)) best = { inst, port, p, d };
    }
  }
  return best ? { inst: best.inst, port: best.port, p: best.p } : null;
}

/** Each host kind's object under the cursor as an end, and the dots it offers:
 *  a text box's nearest side; a part's centre (its ports and anchored points
 *  dotted), else a room's door or heater wall under the cursor, else the room
 *  at that point; a polygon's centre; a wall's or line's point under the
 *  cursor; a floor's centre. */
const CONN_BODY: { [K in ConnHostKind]: (env: ConnPickEnv, world: Vec2) => ConnBody | null } = {
  text: (env, world) => {
    const text = env.textAt(world);
    if (!text) return null;
    const c = textBoxCorners(text);
    const mids = c.map((a, i) => ({ x: (a.x + c[(i + 1) % 4].x) / 2, y: (a.y + c[(i + 1) % 4].y) / 2 }));
    let side = 0;
    mids.forEach((m, i) => { if (dist(world, m) < dist(world, mids[side])) side = i; });
    return { pick: { id: text.id, end: { on: { kind: 'side', side } }, p: mids[side] }, dots: mids };
  },
  instance: (env, world) => {
    const part = env.partAt(world);
    if (part) {
      const centre = { x: part.x, y: part.y };
      return { pick: { id: part.id, p: centre }, dots: [centre, ...portsWorld(part).map((x) => x.p), ...env.anchors(part)] };
    }
    const room = env.roomAt(world);
    if (!room || !room.autoBenches || !isPolyRoom(room)) return null;
    const local = toInstanceLocal(room, world);
    const feats: Array<{ on: ConnOn; p: Vec2; hit: boolean }> = [];
    for (const d of roomDoors(room)) {
      const p = d.id ? onPoint(env.drawing, room.id, { kind: 'door', id: d.id }) : null;
      const rd = roomDoor(room, d);
      if (d.id && p) feats.push({ on: { kind: 'door', id: d.id }, p, hit: !!rd && doorHit(rd, local, env.featureTol) });
    }
    const hw = heaterWallBands(room).find((b) => b.poly.length >= 3 && pointInPolygon(local, b.poly))?.wall ?? null;
    for (const w of heaterWallsOf(room)) {
      const p = w.id ? onPoint(env.drawing, room.id, { kind: 'heaterWall', id: w.id }) : null;
      if (w.id && p) feats.push({ on: { kind: 'heaterWall', id: w.id }, p, hit: hw === w });
    }
    const dots = feats.map((f) => f.p);
    const f = feats.find((x) => x.hit);
    if (f) return { pick: { id: room.id, end: { on: f.on }, p: f.p }, dots };
    const ref: DimAnchor = { instId: room.id, nx: room.width ? local.x / (room.width / 2) : 0, ny: room.depth ? local.y / (room.depth / 2) : 0 };
    return { pick: { id: room.id, end: { ref }, p: world }, dots };
  },
  polygon: (env, world) => {
    const poly = env.polygonAt(world);
    const centre = poly ? objectCentre(env.drawing, poly.id) : null;
    return poly && centre ? { pick: { id: poly.id, p: centre }, dots: [centre, ...(poly.ellipse ? [] : poly.points)] } : null;
  },
  wall: (env, world) => {
    const wall = env.wallAt(world);
    const along = wall ? alongAt(wall.points, false, world) : null;
    return wall && along ? { pick: { id: wall.id, end: { on: { kind: 'along', seg: along.seg, t: along.t } }, p: along.p }, dots: wall.points } : null;
  },
  flooring: (env, world) => {
    const floor = env.floorAt(world);
    const fc = floor ? objectCentre(env.drawing, floor.id) : null;
    return floor && fc ? { pick: { id: floor.id, p: fc }, dots: [fc] } : null;
  },
};

/** The object under `world` an end can glue to, in Select's pick order
 *  (`CONN_HOST_KINDS`), with the dots it offers. Every object's centre is a
 *  target too: offered as a dot, and glued to (the centre end, which follows the
 *  object) within the aperture of it — the ○ Centre marker says so. */
export function connTargetAt(env: ConnPickEnv, world: Vec2): ConnBody | null {
  let t: ConnBody | null = null;
  for (const k of CONN_HOST_KINDS) if ((t = CONN_BODY[k](env, world))) break;
  if (!t) return null;
  const c = objectCentre(env.drawing, t.pick.id);
  if (!c) return t;
  const dots = [c, ...t.dots.filter((d) => dist(d, c) > 1e-6)];
  if (dist(world, c) > env.aperture) return { pick: t.pick, dots };
  env.markCentre?.(c);
  return { pick: { id: t.pick.id, p: c }, dots };
}

/** What a pick at `world` attaches to (the module's order); `free` (Alt): a
 *  free point where the pointer is. */
export function connPickAt(env: ConnPickEnv, world: Vec2, free = false): ConnPick {
  if (free) return { id: '', end: { p: world }, p: world };
  const port = portAt(env, world);
  if (port) {
    const ref: DimAnchor = { instId: port.inst.id, nx: port.port.nx, ny: port.port.ny };
    return { id: port.inst.id, end: { port: port.port.id, ref }, p: port.p, type: port.port.type };
  }
  const s = env.snap(world);
  if (s.anchor) {
    // A room corner keeps its vertex tag, so a reshaped room keeps the end on it.
    const { feat, ...rest } = s.anchor;
    const ref: DimAnchor = feat?.kind === 'polyv' ? { ...rest, feat } : rest;
    return { id: anchorObjectId(ref), end: { ref }, p: s.point };
  }
  // A snap with no anchor — a midpoint, the nearest point, a perpendicular foot,
  // an intersection — still lies ON some object: glue along that object's
  // outline, so the end follows it (a side's midpoint stays its midpoint)
  // instead of being left behind as a free point.
  if (s.snapped) {
    const glued = outlineGlueAt(env, s.point);
    if (glued) return glued;
  }
  const hit = connTargetAt(env, world);
  if (hit) return hit.pick;
  return { id: '', end: { p: s.point }, p: s.point };
}

/** Each host kind's objects an acquired snap may glue along, topmost first, and
 *  how far off its outline a point may lie and still be on it: a thick wall's or
 *  a room's wall thickness (its centre line / outer face), else none. */
const GLUE_BANDS: { [K in ConnHostKind]: (env: ConnPickEnv) => Array<{ id: string; band: number }> } = {
  text: ({ drawing: d }) => (d.texts || []).slice().reverse().map((t) => ({ id: t.id, band: 0 })),
  instance: ({ drawing: d, partShown }) => [
    ...d.instances.filter((i) => !i.autoBenches && partShown(i)).reverse().map((i) => ({ id: i.id, band: 0 })),
    ...d.instances.filter((i) => i.autoBenches && partShown(i)).reverse().map((i) => ({ id: i.id, band: wallThicknessOf(i) })),
  ],
  polygon: ({ drawing: d }) => (d.polygons || []).slice().reverse().map((q) => ({ id: q.id, band: 0 })),
  wall: ({ drawing: d }) => (d.walls || []).slice().reverse().map((w) => ({ id: w.id, band: w.style === 'line' ? 0 : w.thickness })),
  flooring: ({ drawing: d }) => (d.floorings || []).slice().reverse().map((f) => ({ id: f.id, band: 0 })),
};

/** The object whose outline passes through `p` (a snapped point), in Select's
 *  pick order, as an `along` end. A thick wall or a room's wall is matched within
 *  its thickness of the line it is measured on (its centre line / outer face),
 *  and the point keeps that offset, so a face's midpoint stays on the face. */
export function outlineGlueAt(env: ConnPickEnv, p: Vec2): ConnPick | null {
  const d = env.drawing, tol = env.outlineTol;
  for (const c of CONN_HOST_KINDS.flatMap((k) => GLUE_BANDS[k](env))) {
    const o = connOutline(d, c.id);
    const a = o ? alongAt(o.pts, o.closed, p) : null;
    if (!o || !a || dist(a.p, p) > c.band + tol) continue;
    const A = o.pts[a.seg], B = o.pts[(a.seg + 1) % o.pts.length];
    const L = Math.hypot(B.x - A.x, B.y - A.y) || 1;
    const off = ((B.x - A.x) * (p.y - A.y) - (B.y - A.y) * (p.x - A.x)) / L;
    const on: ConnOn = Math.abs(off) > tol ? { kind: 'along', seg: a.seg, t: a.t, off } : { kind: 'along', seg: a.seg, t: a.t };
    return { id: c.id, end: { on }, p };
  }
  return null;
}
